import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, MenuItem } from '@mui/material';
import ResultCard from './ResultCard';
import Pagination from './Pagination';
import MessageDisplay from './MessageDisplay';
import { setPreferences } from '../../store/searchSlice';
import { 
  RESULTS_PER_PAGE_OPTIONS, 
  SORT_OPTIONS,
  DEFAULT_RESULTS_PER_PAGE,
  DEFAULT_SORT 
} from '../../constants/search';
import './ResultsGrid.css';

function ResultsGrid() {
  const dispatch = useDispatch();
  const reduxState = useSelector((state) => state.search);
  const { preferences, results: allResults, loading, error } = reduxState;
  
  const { 
    perPage = DEFAULT_RESULTS_PER_PAGE, 
    sortBy = DEFAULT_SORT,
    currentPage = 1
  } = preferences;

  // Sort and paginate results locally
  const displayedResults = useMemo(() => {
    if (!allResults?.length) return [];
    
    // First sort the results
    let sortedResults = [...allResults];
    switch (sortBy) {
      case 'price-asc':
        sortedResults.sort((a, b) => a.PriceDetails.PricePerAdult - b.PriceDetails.PricePerAdult);
        break;
      case 'price-desc':
        sortedResults.sort((a, b) => b.PriceDetails.PricePerAdult - a.PriceDetails.PricePerAdult);
        break;
      case 'date-asc':
        sortedResults.sort((a, b) => new Date(a.FlightDetails.Outbound.DepartureDateTime) - new Date(b.FlightDetails.Outbound.DepartureDateTime));
        break;
      case 'date-desc':
        sortedResults.sort((a, b) => new Date(b.FlightDetails.Outbound.DepartureDateTime) - new Date(a.FlightDetails.Outbound.DepartureDateTime));
        break;
      case 'stars-desc':
        sortedResults.sort((a, b) => b.HotelDetails.StarRating - a.HotelDetails.StarRating);
        break;
      case 'stars-asc':
        sortedResults.sort((a, b) => a.HotelDetails.StarRating - b.HotelDetails.StarRating);
        break;
      default:
        break;
    }

    // Then paginate
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    return sortedResults.slice(start, end);
  }, [allResults, sortBy, currentPage, perPage]);

  // Scroll to top when displayed results change
  useEffect(() => {
    if (displayedResults.length > 0) {
      const resultsContainer = document.querySelector('.results-container');
      if (resultsContainer) {
        resultsContainer.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  }, [displayedResults]);

  // Populate window.travelfind object for Claiva extraction template
  useEffect(() => {
    try {
      // Calculate pagination values for this effect
      const totalResultsCount = allResults?.length || 0;
      const totalPagesCount = Math.ceil(totalResultsCount / perPage);
      
      // Transform displayed results to Claiva-compatible format
      const claivaPackages = displayedResults.map(packageHoliday => ({
      // Unique identifier for linking to placeholders
      ItemKey: packageHoliday.ItemKey,
      
      // Core required fields for ItemKey generation
      Brand: packageHoliday.Brand || 'OBD',
      HolidayCountry: packageHoliday.HolidayCountry,
      HolidayRegion: packageHoliday.HolidayRegion,
      HolidayResort: packageHoliday.HolidayResort,
      Duration: packageHoliday.Duration,
      BoardBasis: packageHoliday.BoardBasis,
      RoomTypeDescription: packageHoliday.RoomTypeDescription,
      FreeChild: false, // Default value
      HotelIdentifierCode: packageHoliday.HotelDetails?.HotelIdentifierCode,
      FlightNumber_Outbound: packageHoliday.FlightDetails?.Outbound?.FlightNumber,
      DepartureAirportCode_Outbound: packageHoliday.FlightDetails?.Outbound?.DepartureAirportCode,
      FlightNumber_Inbound: packageHoliday.FlightDetails?.Inbound?.FlightNumber,
      DepartureAirportCode_Inbound: packageHoliday.FlightDetails?.Inbound?.DepartureAirportCode,
      DepartureDate: packageHoliday.FlightDetails?.Outbound?.DepartureDateTime?.split('T')[0], // Extract date from datetime
      
      // Analysis fields
      NumberOfAdults: packageHoliday.PriceDetails?.NumberOfAdults || 2,
      NumberOfChildren: packageHoliday.PriceDetails?.NumberOfChildren || 0,
      NumberOfInfants: packageHoliday.PriceDetails?.NumberOfInfants || 0,
      TotalPrice: packageHoliday.PriceDetails?.TotalPrice,
      
      // Hotel section (schemaless)
      hotel: {
        HotelName: packageHoliday.HotelDetails?.HotelName,
        StarRating: packageHoliday.HotelDetails?.StarRating,
        ConsumerRating: packageHoliday.HotelDetails?.ConsumerRating,
        Latitude: packageHoliday.HotelDetails?.Latitude,
        Longitude: packageHoliday.HotelDetails?.Longitude,
        HotelURL: packageHoliday.HotelDetails?.HotelURL,
        HotelImages: packageHoliday.HotelDetails?.HotelImages?.[0] // First image
      },
      
      // Flight section (schemaless)
      flight: {
        DepartureDateTime_Outbound: packageHoliday.FlightDetails?.Outbound?.DepartureDateTime,
        ArrivalDateTime_Outbound: packageHoliday.FlightDetails?.Outbound?.ArrivalDateTime,
        ArrivalAirportCode_Outbound: packageHoliday.FlightDetails?.Outbound?.ArrivalAirportCode,
        AirlineName_Outbound: packageHoliday.FlightDetails?.Outbound?.AirlineName,
        NumberOfStops_Outbound: packageHoliday.FlightDetails?.Outbound?.NumberOfStops,
        FlightDuration_Outbound: packageHoliday.FlightDetails?.Outbound?.FlightDuration,
        DepartureDateTime_Inbound: packageHoliday.FlightDetails?.Inbound?.DepartureDateTime,
        ArrivalDateTime_Inbound: packageHoliday.FlightDetails?.Inbound?.ArrivalDateTime,
        ArrivalAirportCode_Inbound: packageHoliday.FlightDetails?.Inbound?.ArrivalAirportCode,
        AirlineName_Inbound: packageHoliday.FlightDetails?.Inbound?.AirlineName,
        NumberOfStops_Inbound: packageHoliday.FlightDetails?.Inbound?.NumberOfStops,
        FlightDuration_Inbound: packageHoliday.FlightDetails?.Inbound?.FlightDuration,
        TransfersIncluded: packageHoliday.PriceDetails?.Extras?.TransfersIncluded,
        LuggageIncluded: packageHoliday.PriceDetails?.Extras?.LuggageIncluded
      },
      
      // Additional data section (schemaless)
      additionalData: {
        Currency: packageHoliday.PriceDetails?.Currency || 'GBP',
        PricePerAdult: packageHoliday.PriceDetails?.PricePerAdult,
        TotalPriceBeforeDiscount: packageHoliday.PriceDetails?.TotalPriceBeforeDiscount,
        AdjustedPrice: packageHoliday.PriceDetails?.AdjustedPrice,
        DeeplinkUrl: packageHoliday.DeeplinkUrl,
        Bonded: packageHoliday.PriceDetails?.Extras?.Bonded,
        CacheKey: packageHoliday.CacheKey,
        LastUpdate: packageHoliday.LastUpdate,
        Expiry: packageHoliday.Expiry
      }
    }));

    // Create the global travelfind object for Claiva extraction template
    window.travelfind = {
      // Current page results
      currentPageResults: claivaPackages,
      
      // Pagination info
      pagination: {
        currentPage,
        totalPages: totalPagesCount,
        totalResults: totalResultsCount,
        perPage
      },
      
      // Search/filter state
      searchState: {
        sortBy,
        allResultsCount: totalResultsCount
      },
      
      // Timestamp for cache busting
      lastUpdated: new Date().toISOString()
    };

      // Log for debugging (remove in production)
      console.log('Updated window.travelfind with', claivaPackages.length, 'packages');
      
    } catch (error) {
      console.warn('Failed to populate window.travelfind object (this will not affect site functionality):', error);
      
      // Ensure basic object exists even if population fails
      window.travelfind = window.travelfind || {
        currentPageResults: [],
        pagination: { currentPage: 1, totalPages: 0, totalResults: 0, perPage: 10 },
        searchState: { sortBy: 'default', allResultsCount: 0 },
        lastUpdated: new Date().toISOString()
      };
    }
    
  }, [displayedResults, currentPage, perPage, sortBy, allResults]);

  // Calculate pagination values
  const totalResults = allResults?.length || 0;
  const totalPages = Math.ceil(totalResults / perPage);

  const handleSortChange = (event) => {
    dispatch(setPreferences({ 
      sortBy: event.target.value,
      currentPage: 1 // Reset to first page when sorting changes
    }));
  };

  const handlePerPageChange = (event) => {
    dispatch(setPreferences({ 
      perPage: Number(event.target.value),
      currentPage: 1 // Reset to first page when per page changes
    }));
  };

  const handlePageChange = (newPage) => {
    dispatch(setPreferences({ currentPage: newPage }));
  };

  if (loading) return <MessageDisplay message="Loading results..." />;
  if (error) return <MessageDisplay message={error} />;
  if (!allResults?.length) return (
    <MessageDisplay message="No holidays found matching your search criteria. Try adjusting your filters or search for different dates." />
  );

  return (
    <>
      <div className="results-container">
        <div className="container">
          <div className="results-header">
            <div className="results-count">
              Showing {(currentPage - 1) * perPage + 1} to {
                Math.min(currentPage * perPage, totalResults)
              } of {totalResults} results
            </div>
            
            <div className="results-controls">
              <div className="sort-by">
                <span>Sort by:</span>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  size="small"
                >
                  {SORT_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="results-per-page">
                <span>Results per page:</span>
                <Select
                  value={perPage}
                  onChange={handlePerPageChange}
                  size="small"
                >
                  {RESULTS_PER_PAGE_OPTIONS.map(value => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          
          {displayedResults.map(packageHoliday => (
            <ResultCard 
              key={packageHoliday.ItemKey} 
              packageHoliday={packageHoliday} 
            />
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );
}

export default ResultsGrid;
